import classNames from 'classnames'
import { Typography, Box, Divider, CircularProgress } from '@mui/material'
import { Waypoint } from 'react-waypoint'

import TrayManagementFilters from 'components/organisms/TrayManagementFilters/TrayManagementFilters'
import EditTrayModal from 'components/organisms/EditTrayModal/EditTrayModal'
import EditTrayStatusModal from 'components/organisms/EditTrayStatusModal/EditTrayStatusModal'
import TrayRejectionDetailsModal from 'components/organisms/TrayRejectionDetailsModal/TrayRejectionDetailsModal'
import { ChooseTrayLocationModal } from 'components/organisms/ChooseTrayLocationModal/ChooseTrayLocationModal'
import EditTrayCategoryModal from 'components/organisms/EditTrayCategoryModal/EditTrayCategoryModal'

import { useTrayManagementContext } from './TrayManagement.context'
import useTrayManagementLogic from './TrayManagement.logic'

import { ItemResponse } from 'common/types'
import './TrayManagement.scss'

const TrayManagement = () => {
  const {
    trays,
    setTrays,
    loadingTrays,
    error,
    isFiltersNavbarOpen,
    isListOfSurgeriesOpen,
    selectedTray,
    totalCount,
    isRefetching,
    // Separate pagination data
    assignedTrays,
    unassignedTrays,
    assignedPagination,
    unassignedPagination,
    loadMoreAssigned,
    loadMoreUnassigned,
  } = useTrayManagementContext()

  const {
    handleListOfSurgeriesClose,
    isEditTrayStatusModalOpen,
    handleEditTrayStatusClick,
    handleEditTrayStatusChange,
    selectedTrayCategory,
    isRejectionDetailsModalOpen,
    rejectionDetails,
    handleRejectionDetailsModalClose,
    isShowChooseTrayLocationModalVisible,
    handleChooseTrayLocationModalClose,
    groupTraysByCase,
    createTrayCardRenderer,
    createCaseGroupRenderer,
    isUpdatingTrayStatus,
    isEditTrayCategoryModalOpen,
    trayCategoryEditTarget,
    updatingTrayCategory,
    selectedTrayCategoryEdit,
    setSelectedTrayCategoryEdit,
    handleEditTrayCategoryModalClose,
    handleUpdateTrayCategory,
  } = useTrayManagementLogic()

  const {
    caseGroups: assignedCaseGroups,
    unassignedTrays: assignedUnassignedTrays,
  } = groupTraysByCase(assignedTrays)
  const { unassignedTrays: unassignedUnassignedTrays } =
    groupTraysByCase(unassignedTrays)
  const renderTrayCard = createTrayCardRenderer()
  const renderCaseGroup = createCaseGroupRenderer(renderTrayCard)

  const handleInfiniteScrollAssigned = () => {
    if (
      assignedPagination.hasNextPage &&
      !assignedPagination.loading &&
      !isRefetching
    ) {
      loadMoreAssigned()
    }
  }

  const handleInfiniteScrollUnassigned = () => {
    if (
      unassignedPagination.hasNextPage &&
      !unassignedPagination.loading &&
      !isRefetching
    ) {
      loadMoreUnassigned()
    }
  }

  const trayCategoryOptions = [
    { value: 'facility', label: 'Facility Owned Tray' },
    { value: 'consigned', label: 'Consigned Tray' },
    { value: 'loaner', label: 'Loaner Tray' },
  ]

  return (
    <div
      data-testid="tray-management_wrapper"
      className="tray-management_wrapper"
    >
      <div className="filters_container">
        <TrayManagementFilters />
      </div>

      <div
        className={classNames('content_container', {
          open: isFiltersNavbarOpen,
        })}
      >
        {loadingTrays && trays.length === 0 ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading trays...</Typography>
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography color="error">Error loading trays</Typography>
          </Box>
        ) : (
          <>
            {/* Total Count Display */}
            {totalCount > 0 && (
              <Box mb={2}>
                <Typography variant="h6" className="total-count">
                  Total Trays: {totalCount} (Assigned:{' '}
                  {assignedPagination.totalCount}, Unassigned:{' '}
                  {unassignedPagination.totalCount})
                </Typography>
              </Box>
            )}

            {/* Assigned Trays Section */}
            {Object.keys(assignedCaseGroups).length > 0 && (
              <Box mb={4}>
                <Typography variant="h5" className="section-title">
                  Assigned Trays (
                  {Object.values(assignedCaseGroups).flat().length}
                  {assignedPagination.hasNextPage ? '+' : ''})
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" flexDirection="column" gap={2}>
                  {Object.entries(assignedCaseGroups).map(
                    ([caseId, caseTrays], index) => {
                      const isLastAssignedGroup =
                        index === Object.entries(assignedCaseGroups).length - 1

                      return (
                        <div key={caseId}>
                          {isLastAssignedGroup &&
                            assignedPagination.hasNextPage && (
                              <Waypoint
                                onEnter={handleInfiniteScrollAssigned}
                              />
                            )}
                          {renderCaseGroup(caseId, caseTrays)}
                        </div>
                      )
                    }
                  )}
                </Box>

                {/* Loading More Assigned Indicator */}
                {assignedPagination.loading && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    p={2}
                    className="infinite-scroll-loader"
                  >
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 1 }}>
                      Loading more assigned trays...
                    </Typography>
                  </Box>
                )}

                {/* End of Assigned Results Indicator */}
                {!assignedPagination.hasNextPage &&
                  assignedTrays.length > 0 && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      p={2}
                    >
                      <Typography variant="body2" color="textSecondary">
                        All assigned trays loaded
                      </Typography>
                    </Box>
                  )}
              </Box>
            )}

            {/* Unassigned Trays Section */}
            {(unassignedUnassignedTrays.length > 0 ||
              assignedUnassignedTrays.length > 0) && (
              <Box>
                <Typography variant="h5" className="section-title">
                  Unassigned Trays (
                  {unassignedUnassignedTrays.length +
                    assignedUnassignedTrays.length}
                  {unassignedPagination.hasNextPage ? '+' : ''})
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" flexDirection="column" gap={2}>
                  {/* Render unassigned trays from assigned query */}
                  {assignedUnassignedTrays.map((tray, index) => (
                    <div key={`assigned-unassigned-${tray.id}`}>
                      {renderTrayCard(tray)}
                    </div>
                  ))}

                  {/* Render unassigned trays from unassigned query */}
                  {unassignedUnassignedTrays.map((tray, index) => {
                    const isLastUnassignedTray =
                      index === unassignedUnassignedTrays.length - 1

                    return (
                      <div key={`unassigned-${tray.id}`}>
                        {isLastUnassignedTray &&
                          unassignedPagination.hasNextPage && (
                            <Waypoint
                              onEnter={handleInfiniteScrollUnassigned}
                            />
                          )}
                        {renderTrayCard(tray)}
                      </div>
                    )
                  })}
                </Box>

                {/* Loading More Unassigned Indicator */}
                {unassignedPagination.loading && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    p={2}
                    className="infinite-scroll-loader"
                  >
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 1 }}>
                      Loading more unassigned trays...
                    </Typography>
                  </Box>
                )}

                {/* End of Unassigned Results Indicator */}
                {!unassignedPagination.hasNextPage &&
                  (unassignedUnassignedTrays.length > 0 ||
                    assignedUnassignedTrays.length > 0) && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      p={2}
                    >
                      <Typography variant="body2" color="textSecondary">
                        All unassigned trays loaded
                      </Typography>
                    </Box>
                  )}
              </Box>
            )}

            {/* Empty State */}
            {trays.length === 0 && !loadingTrays && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={4}
              >
                <Typography>No trays found matching your filters</Typography>
              </Box>
            )}
          </>
        )}
      </div>

      <EditTrayStatusModal
        open={isEditTrayStatusModalOpen}
        onClose={() => handleEditTrayStatusClick(undefined)}
        onConfirm={handleEditTrayStatusChange}
        trayCategory={selectedTrayCategory}
        isUpdatingTrayStatus={isUpdatingTrayStatus}
      />

      {selectedTray && (
        <EditTrayModal
          open={isListOfSurgeriesOpen && !!selectedTray}
          onClose={handleListOfSurgeriesClose}
          trayData={selectedTray?.trayItem as ItemResponse}
        />
      )}

      <TrayRejectionDetailsModal
        isOpen={isRejectionDetailsModalOpen}
        onClose={handleRejectionDetailsModalClose}
        rejectionDetails={rejectionDetails}
      />

      <ChooseTrayLocationModal
        open={isShowChooseTrayLocationModalVisible}
        onClose={handleChooseTrayLocationModalClose}
        setTrays={setTrays}
      />

      <EditTrayCategoryModal
        open={isEditTrayCategoryModalOpen && !!trayCategoryEditTarget}
        onClose={handleEditTrayCategoryModalClose}
        onConfirm={handleUpdateTrayCategory}
        selectedCategory={selectedTrayCategoryEdit}
        setSelectedCategory={(cat) =>
          setSelectedTrayCategoryEdit(
            cat as 'facility' | 'consigned' | 'loaner'
          )
        }
        isUpdating={updatingTrayCategory}
        trayCategoryOptions={trayCategoryOptions}
      />
    </div>
  )
}

export default TrayManagement
